// frontend/src/features/ups/UpsReservedDriverList.js
// This React component renders the UPS Reserved drivers list, honoring global filters,
// and letting users activate drivers or—if admin—ban them via a reason modal.
//
// Phase 6:
// - READ: Reserved drivers come from assignmentsSlice (confirmed assignments) instead of driverSlice.
// - REALTIME: Option A => no component-level socket listeners (handled centrally in services/socket.js).
// - WRITES: activate/ban stay the same; after success we reload confirmed assignments so rows move immediately.
// - MODAL FIX: when opening details from an assignment-based row, upsert the driver into driverSlice first
//              so DriverDetailsModal can prefer Redux + receive live socket updates.
//
// Phase 7:
// - Use selectedSiteId (from sitesSlice) instead of hardcoding MAIN.
//
// Phase 8.7D cleanup:
// - Do NOT pass agencyId/search into loadSiteAssignments payload (thunk reads from Redux).
// - Keep effect deps on agencyId/q so refetch occurs when filters change.

// Phase 09 note:
// - This tab is intentionally NOT time-bucket-specific.
// - Reserved is a standby / capacity view, not a time-sliced operational lane.
// - Primary truth here is assignment_status = 'confirmed'.
// - Therefore we use an explicit broad query:
//     statuses: ['confirmed']
//     timeBucket: undefined
// - This makes the intent clear during Phase 09 cleanup:
//   time filtering is intentionally not applied here yet.
// - However, we still preserve canonical starts_at / ends_at on the adapted row model
//   so the UI remains aligned with the Phase 09 time model.

import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { UserCheck, Eye, Ban as BannedIcon } from 'lucide-react';

import { selectIsUpsAdmin } from '../auth/authSlice';
import { selectAllAgencies } from '../agencies/agenciesSlice';

// ✅ Phase 7: site context
import { selectSelectedSiteId } from '../sites/sitesSlice';

// ✅ Phase 6: switch from drivers slice to assignments slice (read path)
import { loadSiteAssignments, makeSelectAssignmentsForTab } from '../assignments/assignmentsSlice';

import { selectFilters, bumpStatsRefreshKey } from '../ui/filtersSlice';

import { activateDriver, banDriver } from '../../services/upsDriverService';
import DriverDetailsModal from './modals/DriverDetailsModal';

// ✅ Modal live-update fix: prime driverSlice for DriverDetailsModal
import { upsertDriver } from '../drivers/driverSlice';
import { formatOwnerLabel } from './utils/formatOwnerLabel';

const normalizeId = (v) => (v == null ? undefined : Number(v));

const RESERVED_ASSIGNMENT_QUERY = {
  statuses: ['confirmed'],
  timeBucket: undefined,
};

export default function UpsReservedDriverList() {
  const dispatch = useDispatch();
  const banReasonId = 'reserved-driver-ban-reason';
  const isAdmin = useSelector(selectIsUpsAdmin);
  const agencies = useSelector(selectAllAgencies);
  const { agencyId, q, source } = useSelector(selectFilters);

  // ✅ Phase 7: selected site id from global SiteSelector
  const selectedSiteId = useSelector(selectSelectedSiteId);

  // ✅ Reserved UI == assignments.status 'confirmed'
  // Broad by design: this is a standby reserve view, not a time-bucketed slice.
useEffect(() => {
  if (!selectedSiteId) return;

  dispatch(loadSiteAssignments({ siteId: selectedSiteId, ...RESERVED_ASSIGNMENT_QUERY }));
}, [dispatch, agencyId, q, source, selectedSiteId]);

  // ✅ Select "confirmed" assignment rows from assignmentsSlice
  const selectReservedAssignments = useMemo(() => makeSelectAssignmentsForTab('confirmed'), []);
  const rows = useSelector(selectReservedAssignments);

  // ✅ Adapt confirmed assignment rows into a UI row model with embedded driver fields.
  // Operational truth remains assignment_status; this is not a driver-status row.
  // Internal/company-owned reserved rows should render deliberately, not as blank agency values
  const reservedAssignments = useMemo(
    () =>
      (rows || [])
        // ✅ Reserved tab must not show banned drivers (banned belongs in Banned tab)
        .filter((r) => !r?.is_banned)
        .map((r) => ({
          // driver fields (your backend row likely returns driver columns)
          id: normalizeId(r.id),
          name: r.name,
          vehicle_registration_number: r.vehicle_registration_number,
          vrn: r.vrn, // fallback
          phone: r.phone,
          email: r.email,
          submission_date: r.submission_date,
          agency_id: r.agency_id,
          agency_name: r.agency_name,
          // Phase 9.1 ownership context
          workforce_source: r.workforce_source ?? null,
          company_id: normalizeId(r.company_id),
          photo_url: r.photo_url,
          photo_mime: r.photo_mime,
          photo_updated_at: r.photo_updated_at,

          // assignment metadata (useful later)
          assignment_id: r.assignment_id,
          assignment_status: r.assignment_status,
          site_id: normalizeId(r.site_id),
          site_name: r.site_name ?? null,

          // Phase 09 canonical time carry-through
          starts_at: r.starts_at ?? r.start_time ?? null,
          ends_at: r.ends_at ?? r.end_time ?? null,

          // Keep legacy time fields during dual-read transition
          start_time: r.start_time ?? null,
          end_time: r.end_time ?? null,

          // ban metadata (useful later)
          is_banned: r.is_banned,
          ban_id: r.ban_id,
          ban_scope: r.ban_scope,
          ban_site_id: r.ban_site_id,
          ban_reason: r.ban_reason,
          banned_at: r.banned_at,
        })),
    [rows]
  );

  // Focus-on-arrival via ?focus=<id>
  const [sp, setSp] = useSearchParams();
  useEffect(() => {
    const focus = sp.get('focus');
    if (!focus) return;
    const el = document.querySelector(`[data-row-id="${focus}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.classList.add('ring-2', 'ring-blue-400', 'animate-pulse');
    const t = setTimeout(() => {
      el.classList.remove('ring-2', 'ring-blue-400', 'animate-pulse');
      const next = new URLSearchParams(sp);
      next.delete('focus');
      setSp(next, { replace: true });
    }, 1600);
    return () => clearTimeout(t);
  }, [sp, setSp]);

  // ✅ Writes stay the same; after success, reload confirmed assignments for current site.
  const handleActivate = async (id) => {
    if (!selectedSiteId) return;
    try {
      await activateDriver(id, selectedSiteId);

      dispatch(loadSiteAssignments({ siteId: selectedSiteId, ...RESERVED_ASSIGNMENT_QUERY }));
      dispatch(bumpStatsRefreshKey());
    } catch (err) {
      console.error('Activate failed:', err);
    }
  };

  // Ban modal (same UX as Organized list)
  const [showBanModal, setShowBanModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [banReason, setBanReason] = useState('');

  const openBanModal = (driver) => {
    if (!isAdmin) return;

    // Prime driverSlice so any follow-up modals / details have a live Redux-backed entity
    dispatch(upsertDriver(driver));

    setSelectedDriver(driver);
    setBanReason('');
    setShowBanModal(true);
  };

  const handleBanConfirm = async () => {
    if (!selectedSiteId) return;
    if (!banReason.trim() || !isAdmin) return;

    try {
      await banDriver(selectedDriver.id, banReason);

      // ✅ After banning, reload confirmed assignments for current site.
      dispatch(loadSiteAssignments({ siteId: selectedSiteId, ...RESERVED_ASSIGNMENT_QUERY }));
      dispatch(bumpStatsRefreshKey());

      setShowBanModal(false);
      setSelectedDriver(null);
    } catch (err) {
      console.error('Ban failed:', err);
    }
  };

  // Details modal
  const [showDetails, setShowDetails] = useState(false);
  const [detailsDriver, setDetailsDriver] = useState(null);

  // ✅ Phase 6 fix: ensure the driver exists in driverSlice so the details modal can
  // prefer Redux and receive live socket updates (photo updates, driverUpdated, etc.)
  const openDetails = (driver) => {
    dispatch(upsertDriver(driver));
    setDetailsDriver(driver);
    setShowDetails(true);
  };

  const closeDetails = () => {
    setShowDetails(false);
    setDetailsDriver(null);
  };

  // Empty-state labels
  const scopeLabel = (() => {
    if (!agencyId) return 'All Agencies';
    const a = (agencies || []).find(
      (x) => String(x.id ?? x.agency_id ?? x.agencyId) === String(agencyId)
    );
    return a?.name ?? a?.agency_name ?? `Agency #${agencyId}`;
  })();

  const hasSearch = (q || '').trim().length > 0;

  return (
    <>
      <div className="overflow-x-auto my-6">
        <h3 className="text-2xl font-bold mb-4 text-gray-800">Reserved Drivers</h3>
        <table className="min-w-full bg-white rounded-lg shadow overflow-hidden">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {['Name', 'Vehicle Reg.', 'Phone', 'Email', 'Owner', 'Submitted', 'Actions'].map((col) => (
                <th key={col} className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase">
                  {col}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200">
            {reservedAssignments.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                  No reserved drivers{agencyId ? ` for ${scopeLabel}` : ''}
                  {hasSearch ? ` matching “${q}”` : ''}.
                </td>
              </tr>
            ) : (
              reservedAssignments.map((row) => (
                <tr key={row.id} data-row-id={row.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">{row.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {row.vehicle_registration_number || row.vrn || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{row.phone}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{row.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {formatOwnerLabel(row)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {row.submission_date ? new Date(row.submission_date).toLocaleString() : '—'}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-4">
                      {/* Details first */}
                      <button
                        onClick={() => openDetails(row)}
                        aria-label="View driver details"
                        title="View details"
                        className="p-1 hover:bg-gray-100 rounded text-blue-600"
                      >
                        <Eye size={16} />
                      </button>

                      {/* Activate (disable if banned) */}
                      <button
                        onClick={() => handleActivate(row.id)}
                        aria-label="Activate driver"
                        title={row.is_banned ? `Banned: ${row.ban_reason || '—'}` : 'Activate'}
                        disabled={!!row.is_banned}
                        className={
                          'p-1 rounded ' +
                          (row.is_banned
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'hover:bg-gray-100 text-green-600')
                        }
                      >
                        <UserCheck size={16} />
                      </button>

                      {/* Ban (admins only) */}
                      <button
                        onClick={() => openBanModal(row)}
                        aria-label="Ban driver"
                        title={isAdmin ? 'Ban' : 'Admins only'}
                        disabled={!isAdmin}
                        className={
                          'p-1 rounded ' +
                          (isAdmin ? 'hover:bg-gray-100 text-red-600' : 'bg-gray-200 text-gray-400 cursor-not-allowed')
                        }
                      >
                        <BannedIcon size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Ban Modal */}
      {showBanModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowBanModal(false)}
        >
          <div className="bg-white rounded-lg shadow-md p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Ban {selectedDriver?.name}</h2>
              <button
                onClick={() => setShowBanModal(false)}
                className="text-gray-500 hover:text-gray-700 text-xl font-bold"
                aria-label="Close ban modal"
              >
                &times;
              </button>
            </div>

            <label htmlFor={banReasonId} className="block text-sm font-medium text-gray-700 mb-2">
              Ban reason
            </label>
            <textarea
              id={banReasonId}
              name="banReason"
              rows={4}
              placeholder="Enter ban reason"
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-primary"
            />

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowBanModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleBanConfirm}
                disabled={!isAdmin}
                title={isAdmin ? 'Confirm Ban' : 'Admins only'}
                className={
                  'px-4 py-2 rounded ' +
                  (isAdmin ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-gray-200 text-gray-400 cursor-not-allowed')
                }
              >
                Confirm Ban
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Driver Details Modal — standalone */}
      {showDetails && detailsDriver && <DriverDetailsModal driver={detailsDriver} onClose={closeDetails} />}
    </>
  );
}
